import { useCallback } from 'react'
import { message } from 'antd'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import { useSelector } from 'react-redux'

import {
  addBudgetReports,
  approveBudgetReport,
  agedPayableDetailReports,
  agedPayableReports,
  agedReceivableDetailReports,
  agedReceivableReports,
  attachmentsReports,
  balancePayableTerminDetailReports,
  balancePayableTerminReports,
  balanceReceivableTerminDetailReports,
  balanceReceivableTerminReports,
  balanceSheetReports,
  bankSummaryReports,
  budgetDetailReports,
  cashFlowProjectionReports,
  budgetProfitLossDetailReports,
  budgetProfitLossReports,
  budgetReports,
  consolidationBalanceSheetDetailReports,
  consolidationBalanceSheetReports,
  consolidationCashFlowReports,
  consolidationProfitLossReports,
  consolidationWorksheetReports,
  creditMemoReports,
  customerDebtSnapshotDetailReports,
  customerDebtSnapshotReports,
  customerInvoiceDetailReports,
  customerInvoiceReports,
  customerPaymentRecapReports,
  customerReceivableSnapshotDetailReports,
  customerReceivableSnapshotReports,
  debitMemoReports,
  deleteBudgetReports,
  dutyStampsReports,
  editBudgetReports,
  equityMovementReports,
  expenseClaimDetailReports,
  expenseClaimReports,
  expensePerContactReports,
  exportsReports,
  financeReportingsGraphs,
  fixedAssetDetailReports,
  fixedAssetReleaseReports,
  fixedAssetReports,
  generalLedgerAccountsDetailReports,
  generalLedgerAccountsReports,
  generalLedgerReports,
  generalLedgerStatsReports,
  subsidiaryLedgerReports,
  getActivityTeamReports,
  getActivityTeamTransactionReports,
  getCashFlowEndOfPeriod,
  getGeneralLedgerOverviewReports,
  getProductPurchasesContacts,
  getProductPurchasesContactsDetails,
  getProductSalesContacts,
  getProductSalesContactsDetails,
  getSalesPerProductCategory,
  getSalesPerProductCategoryDetail,
  grandTotalInventoryStockMovementReports,
  importsDetailReports,
  importsReports,
  importsTransactionsReports,
  incomePerCustomerDetailReports,
  incomePerCustomerReports,
  inventoryProductSerialExpiresSoonReports,
  inventoryProductSerialExpiryAgeReports,
  inventoryProductSerialHistoryReports,
  inventoryProductSerialStockDetailReports,
  inventoryProductSerialStockMovementDetailReports,
  inventoryStockAdjustmentDetailReports,
  inventoryStockAdjustmentReports,
  inventoryStockMovementDetailFifoReports,
  inventoryStockMovementDetailReports,
  inventoryStockMovementFifoReports,
  inventoryStockMovementReports,
  productStockMovementReports,
  inventorySummaryDetailFifoReports,
  inventorySummaryFifoReports,
  inventorySummaryReports,
  inventoryTurnoverDetailReports,
  inventoryTurnoverReports,
  inventoryWarehouseTransferDetailReports,
  inventoryWarehouseTransferReports,
  invoicePaymentReports,
  invoicePaymentTotalReports,
  journalReports,
  lowStockReports,
  orderPerProductReports,
  paymentConnectKledoDisbursment,
  paymentConnectKledoDisbursmentListDetail,
  paymentConnectKledoTransaction,
  paymentConnectKledoTransactionListDetail,
  productionReports,
  productionAuditReports,
  productionRawMaterialStatsReports,
  productProfitabilityReports,
  productsPriceHistoryReports,
  profitabilityPerInvoiceGrandTotalReports,
  profitabilityPerInvoiceReports,
  profitLossReports,
  purchaseDeliveryReports,
  purchaseDetailReports,
  purchaseInvoicePaymentReports,
  purchaseInvoicePaymentTotalReports,
  purchaseOrderPerProductDetailReports,
  pricelistUnitConversionReports,
  purchaseOrderPerProductReports,
  purchasePerRegionReports,
  purchaseReturnPerProductDetailReports,
  purchaseReturnPerProductReports,
  purchasesPerPeriodReports,
  purchasesPerProductDetailReports,
  purchasesPerProductReports,
  purchasesPerVendorDetailReports,
  purchasesPerVendorReports,
  receivablePayableDetailReports,
  receivablePayableReports,
  rejectBudgetReport,
  revertBudgetReport,
  requestFulfillmentReports,
  salesDeliveryReports,
  salesDetailReports,
  salesPerPeriodReports,
  salesPerPersonDailyReports,
  salesPerPersonReports,
  returnPerProductReports,
  salesPerProductDetailReports,
  salesPerProductReports,
  salesPerRegionReports,
  salesTaxDetailReports,
  salesTaxReports,
  shippingCostPerExpeditionDailyReports,
  shippingCostPerExpeditionReports,
  supplierInvoiceReports,
  trialBalanceReports,
  usageHistoryReports,
  usagePerProjectReports,
  warehouseProductQuotaReports,
  warehouseStockSummaryReports,
  witholdingTaxReports,
} from 'utils/apis'
import { formatBalanceSheet } from 'containers/Reports/BalanceSheet/formatBalanceSheet'
import { userSelector } from 'app-redux/selectors'
import { isDataChangedSince } from 'utils/summaryCache'

// Attach _cache_meta dari response BE ke shape return hook. Await postProcess karena
// bisa berupa async function (mis. formatBalanceSheet yang fetch layout report
// variable).
// Catatan: _cache_meta di-attach sebagai property ENUMERABLE supaya react-query
// structural sharing (replaceEqualDeep) ikut menyalin saat re-fetch. Consumer yang
// iterate via Object.keys / Object.entries pada data ini WAJIB skip key '_cache_meta'
// (lihat EquityMovement & sejenisnya).
const buildReportResult = async (response, postProcess) => {
  const raw = response?.data ?? {}
  const processed = postProcess ? await postProcess(raw) : raw
  const cacheMeta = response?._cache_meta

  if (cacheMeta == null) {
    return processed
  }

  if (Array.isArray(processed)) {
    // Untuk array, attach sebagai named property — iterasi via .map/.forEach/for-of
    // hanya melihat numeric index, jadi tidak bocor. Spread `[...arr]` JUGA hanya
    // copy numeric index (named property hilang) — tapi consumer report kita tidak
    // spread array, hanya akses via index/map. Aman.
    processed._cache_meta = cacheMeta
    return processed
  }

  return { ...processed, _cache_meta: cacheMeta }
}

// Normalize extraKey: terima array atau scalar. Caller kadang pass scalar
// (mis. `extraQueryKey: showPaid`), supaya tetap kompatibel dengan signature lama
// `useQuery(['xxx', payload, scalar], ...)`.
const toExtraKeyArray = (extraKey) => {
  if (extraKey == null) {
    return []
  }
  return Array.isArray(extraKey) ? extraKey : [extraKey]
}

// Buang flag cache-control dari payload sebelum dipakai sebagai queryKey,
// supaya force_refresh/no_cache tidak bikin cache slot react-query baru.
const stripCacheFlags = (payload = {}) => {
  const { force_refresh, no_cache, ...rest } = payload
  return rest
}

// Factory hook standar untuk semua report query: wrap useQuery + attach _cache_meta
// + expose forceRefresh yang kirim force_refresh=1.
//
// queryKeyPrefix: string label untuk react-query (mis. 'profitLossReports')
// apiFn: function API yang menerima payload (mis. profitLossReports)
// payload: object payload dari consumer
// options: react-query options dari consumer
// postProcess: optional transformer atas raw data sebelum di-merge dengan _cache_meta
// extraKey: optional array yang ditambahkan ke queryKey (mis. extraQueryKey / additionalKeys)
//
// options.refreshIfChanged: kirim force_refresh=1 kalau ada simpan/ubah/hapus data
// (utils/summaryCache) setelah query ini terakhir di-fetch. Dipakai summary ber-cache (cache=1).
export const useReportQuery = (queryKeyPrefix, apiFn, payload, options, postProcess, extraKey) => {
  const queryClient = useQueryClient()
  const keyPayload = stripCacheFlags(payload)
  const extra = toExtraKeyArray(extraKey)
  const queryKey = [queryKeyPrefix, keyPayload, ...extra]
  const { refreshIfChanged, ...queryOptions } = options || {}
  // Ada perubahan data sejak terakhir diambil → abaikan staleTime supaya langsung refetch.
  const isDataChanged =
    refreshIfChanged && isDataChangedSince(queryClient.getQueryState(queryKey)?.dataUpdatedAt)

  const query = useQuery(
    queryKey,
    async () => {
      const isChangedOnFetch =
        refreshIfChanged && isDataChangedSince(queryClient.getQueryState(queryKey)?.dataUpdatedAt)
      const { data: response } = await apiFn(
        isChangedOnFetch ? { ...payload, force_refresh: 1 } : payload,
      )
      return buildReportResult(response, postProcess)
    },
    {
      onError: (error) => message.error(error?.data?.message || 'Failed from server!'),
      ...queryOptions,
      ...(isDataChanged && { staleTime: 0 }),
    },
  )

  const forceRefresh = useCallback(
    () =>
      queryClient.fetchQuery(queryKey, async () => {
        const { data: response } = await apiFn({ ...payload, force_refresh: 1 })
        return buildReportResult(response, postProcess)
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [queryClient, payload, ...extra],
  )

  return { ...query, forceRefresh }
}

export const useFinanceReportingsGraphs = ({ payload = {}, extraQueryKey = [], options = {} }) =>
  useReportQuery(
    'financeReportingsGraphs',
    financeReportingsGraphs,
    payload,
    options,
    undefined,
    extraQueryKey,
  )

export const useGetProductSalesContactsDetails = ({ payload = {}, enabled = false }) => {
  return useQuery(
    ['getProductSalesContactDetails', payload],
    async () => {
      const {
        data: { data },
      } = await getProductSalesContactsDetails(payload)
      return data
    },
    {
      enabled,
      keepPreviousData: true,
      onError: (error) => message.error(error?.data?.message || 'Failed data from server!'),
    },
  )
}

export const useGetProductPurchasesContactsDetails = ({ payload = {}, enabled = false }) => {
  return useQuery(
    [`getProductPurchasesContactDetails`, payload],
    async () => {
      const {
        data: { data },
      } = await getProductPurchasesContactsDetails(payload)
      return data
    },
    {
      enabled,
      keepPreviousData: true,
      onError: (error) => message.error(error?.data?.message || 'Failed data from server!'),
    },
  )
}

export const useSalesTaxDetailReport = (payload, options = {}) =>
  useReportQuery('salesTaxDetailReports', salesTaxDetailReports, payload, options)

export const useInventoryStockAdjustmentReport = ({ payload, options }) =>
  useReportQuery(
    'inventoryStockAdjustmentReports',
    inventoryStockAdjustmentReports,
    payload,
    options,
  )

export const useInventoryStockAdjustmentDetailReport = ({ payload, options }) =>
  useReportQuery(
    'inventoryStockAdjustmentDetailReports',
    inventoryStockAdjustmentDetailReports,
    payload,
    options,
  )

export const useLowStockReport = ({ payload, options }) =>
  useReportQuery('lowStockReports', lowStockReports, payload, options)

export const useWarehouseProductQuotaReport = ({ payload, options }) =>
  useReportQuery('warehouseProductQuotaReports', warehouseProductQuotaReports, payload, options)

export const useInventoryWarehouseTransferReport = ({ payload, options }) =>
  useReportQuery(
    'inventoryWarehouseTransferReports',
    inventoryWarehouseTransferReports,
    payload,
    options,
  )

export const useInventoryWarehouseTransferDetailReport = ({ payload, options }) =>
  useReportQuery(
    'inventoryWarehouseTransferDetailReports',
    inventoryWarehouseTransferDetailReports,
    payload,
    options,
  )

// Taxes

export const useSalesTaxReport = ({ payload, options = {} }) =>
  useReportQuery('salesTaxReports', salesTaxReports, payload, {
    keepPreviousData: true,
    ...options,
  })

export const useWitholdingTaxReport = ({ payload, options = {} }) =>
  useReportQuery('witholdingTaxReports', witholdingTaxReports, payload, {
    keepPreviousData: true,
    ...options,
  })

// Expenses

export const useExpensePerContactReport = ({ payload, options }) =>
  useReportQuery('expensePerContactReports', expensePerContactReports, payload, options)

export const useExpenseClaimReport = ({ payload, options }) =>
  useReportQuery('expenseClaimReports', expenseClaimReports, payload, options)

export const useExpenseClaimDetailReports = ({ payload, options = {} }) =>
  useReportQuery(
    'expenseClaimDetailReports',
    (p) => expenseClaimDetailReports(p.id, p),
    payload,
    options,
  )

// Fixed Assets

export const useFixedAssetReports = ({ payload, options }) =>
  useReportQuery('fixedAssetReports', fixedAssetReports, payload, options)

export const useFixedAssetDetailReport = ({ payload, options }) =>
  useReportQuery('fixedAssetDetailReports', fixedAssetDetailReports, payload, options)

export const useFixedAssetReleaseReport = ({ payload, options }) =>
  useReportQuery('fixedAssetReleaseReports', fixedAssetReleaseReports, payload, options)

// Others

export const useAttachmentsReport = ({ payload, enabled = false }) =>
  useReportQuery('attachmentsReports', attachmentsReports, payload, { enabled })

export const useExportsReport = ({ payload, enabled = false }) =>
  useReportQuery('exportsReports', exportsReports, payload, { enabled })

export const useImportsReport = ({ payload, enabled = false }) =>
  useReportQuery('importsReports', importsReports, payload, { enabled })

export const useImportsDetailReport = ({ payload, enabled = false }) => {
  const user = useSelector(userSelector)
  return useReportQuery(
    'importsDetailReports',
    importsDetailReports,
    payload,
    { enabled },
    undefined,
    [user.lang],
  )
}

export const useImportsTransactionsReport = ({ payload, enabled = false }) =>
  useReportQuery('importsTransactionsReports', importsTransactionsReports, payload, { enabled })

// inventory

export const useInventorySummaryReport = ({ payload, options }) =>
  useReportQuery('inventorySummaryReports', inventorySummaryReports, payload, options)

export const useInventorySummaryFifoReports = ({ payload, options }) =>
  useReportQuery('inventorySummaryFifoReports', inventorySummaryFifoReports, payload, options)

export const useInventorySummaryDetailFifoReports = ({ payload, options }) =>
  useReportQuery(
    'inventorySummaryDetailFifoReports',
    inventorySummaryDetailFifoReports,
    payload,
    options,
  )

export const useWarehouseStockSummaryReports = ({ payload, options }) =>
  useReportQuery('warehouseStockSummaryReports', warehouseStockSummaryReports, payload, options)

export const useProductionReport = ({ payload, options }) =>
  useReportQuery('productionReports', productionReports, payload, options)

export const useProductionRawMaterialStatsReport = ({ payload, options }) =>
  useReportQuery(
    'productionRawMaterialStatsReports',
    productionRawMaterialStatsReports,
    payload,
    options,
  )

export const useProductionAuditReport = ({ payload, options }) =>
  useReportQuery('productionAuditReports', productionAuditReports, payload, options)

export const useInventoryStockMovementReport = ({ payload, options }) =>
  useReportQuery('inventoryStockMovementReports', inventoryStockMovementReports, payload, options)

export const useGrandTotalInventoryStockMovementReport = ({ payload, options }) =>
  useReportQuery(
    'grandTotalInventoryStockMovementReports',
    grandTotalInventoryStockMovementReports,
    payload,
    options,
  )

export const useInventoryStockMovementDetailReport = ({ payload, options }) =>
  useReportQuery(
    'inventoryStockMovementDetailReports',
    inventoryStockMovementDetailReports,
    payload,
    options,
  )

export const useInventoryStockMovementFifoReport = ({ payload, options }) =>
  useReportQuery(
    'inventoryStockMovementFifoReports',
    inventoryStockMovementFifoReports,
    payload,
    options,
  )

export const useInventoryStockMovementDetailFifoReport = ({ payload, options }) =>
  useReportQuery(
    'inventoryStockMovementDetailFifoReports',
    inventoryStockMovementDetailFifoReports,
    payload,
    options,
  )

export const useProductStockMovementReport = ({ payload, options }) =>
  useReportQuery('productStockMovementReports', productStockMovementReports, payload, options)

export const useBudgetReport = ({ payload, options }) =>
  useReportQuery('budgetReports', budgetReports, payload, options)

export const useAddBudgetReport = (options = {}) => {
  return useMutation((payload) => addBudgetReports(payload), options)
}

export const useBudgetDetailReport = ({ payload, options }) =>
  useReportQuery(
    'budgetDetailReports',
    (p) => budgetDetailReports(p.id, p.payload),
    payload,
    options,
  )

export const useEditBudgetReport = (options = {}) => {
  return useMutation((payload) => editBudgetReports(payload.id, payload.payload), options)
}

export const useDeleteBudgetReport = (options = {}) => {
  return useMutation((payload) => deleteBudgetReports(payload), options)
}

export const useApproveBudgetReport = (options = {}) => {
  return useMutation((payload) => approveBudgetReport(payload.id, payload.body), options)
}

export const useRejectBudgetReport = (options = {}) => {
  return useMutation((payload) => rejectBudgetReport(payload.id, payload.body), options)
}

export const useRevertBudgetReport = (options = {}) => {
  return useMutation((payload) => revertBudgetReport(payload.id, payload.body), options)
}

export const useBudgetProfitLossReport = ({ payload, options }) =>
  useReportQuery('budgetProfitLossReports', budgetProfitLossReports, payload, options)

export const useBudgetProfitLossDetailReport = ({ payload, options }) =>
  useReportQuery(
    'budgetProfitLossDetailReports',
    (p) => budgetProfitLossDetailReports(p.id, p.payload),
    payload,
    options,
  )

export const useSalesDetailReport = ({ payload, options }) =>
  useReportQuery('salesDetailReports', salesDetailReports, payload, options)

export const usePurchaseDetailReport = ({ payload, options }) =>
  useReportQuery('purchaseDetailReports', purchaseDetailReports, payload, options)

export const useAgedReceivableReport = ({ payload, options }) =>
  useReportQuery('agedReceivableReports', agedReceivableReports, payload, options)

export const useAgedReceivableDetailReport = ({ payload, options }) =>
  useReportQuery('agedReceivableDetailReports', agedReceivableDetailReports, payload, options)

export const useAgedPayableReport = ({ payload, options }) =>
  useReportQuery('agedPayableReports', agedPayableReports, payload, options)

export const useAgedPayableDetailReport = ({ payload, options }) =>
  useReportQuery('agedPayableDetailReports', agedPayableDetailReports, payload, options)

export const useCustomerReceivableSnapshotReport = ({ payload, options }) =>
  useReportQuery(
    'customerReceivableSnapshotReports',
    customerReceivableSnapshotReports,
    payload,
    options,
  )

export const useCustomerReceivableSnapshotDetailReport = ({ payload, options }) =>
  useReportQuery(
    'customerReceivableSnapshotDetailReports',
    customerReceivableSnapshotDetailReports,
    payload,
    options,
  )

export const useCustomerDebtSnapshotReport = ({ payload, options }) =>
  useReportQuery('customerDebtSnapshotReports', customerDebtSnapshotReports, payload, options)

export const useCustomerDebtSnapshotDetailReport = ({ payload, options }) =>
  useReportQuery(
    'customerDebtSnapshotDetailReports',
    customerDebtSnapshotDetailReports,
    payload,
    options,
  )

export const useCustomerInvoiceReport = ({ payload, options }) =>
  useReportQuery('customerInvoiceReports', customerInvoiceReports, payload, options)

export const useCustomerInvoiceDetailReport = ({ payload, options }) =>
  useReportQuery('customerInvoiceDetailReports', customerInvoiceDetailReports, payload, options)

export const useSupplierInvoiceReport = ({ payload, options }) =>
  useReportQuery('supplierInvoiceReports', supplierInvoiceReports, payload, options)

export const useSalesPerProductReport = ({ payload, additionalKeys = [], options }) =>
  useReportQuery(
    'salesPerProductReports',
    salesPerProductReports,
    payload,
    options,
    undefined,
    additionalKeys,
  )

export const useSalesPerProductDetailReport = ({ payload, options }) =>
  useReportQuery('salesPerProductDetailReports', salesPerProductDetailReports, payload, options)

export const useReturnPerProductReport = ({ payload, additionalKeys = [], options }) =>
  useReportQuery(
    'returnPerProductReports',
    returnPerProductReports,
    payload,
    options,
    undefined,
    additionalKeys,
  )

export const usePurchaseReturnPerProductReport = ({ payload, additionalKeys = [], options }) =>
  useReportQuery(
    'purchaseReturnPerProductReports',
    purchaseReturnPerProductReports,
    payload,
    options,
    undefined,
    additionalKeys,
  )

export const usePurchaseReturnPerProductDetailReport = ({ payload, options }) =>
  useReportQuery(
    'purchaseReturnPerProductDetailReports',
    purchaseReturnPerProductDetailReports,
    payload,
    options,
  )

export const useOrderPerProductReport = ({ payload, additionalKeys = [], options }) =>
  useReportQuery(
    'orderPerProductReports',
    orderPerProductReports,
    payload,
    options,
    undefined,
    additionalKeys,
  )

export const useProductProfitabilityReport = ({ payload, options }) =>
  useReportQuery('productProfitabilityReports', productProfitabilityReports, payload, options)

export const usePurchasesPerProductReport = ({ payload, options }) =>
  useReportQuery('purchasesPerProductReports', purchasesPerProductReports, payload, options)

export const usePurchasesPerProductDetailReport = ({ payload, options }) =>
  useReportQuery(
    'purchasesPerProductDetailReports',
    purchasesPerProductDetailReports,
    payload,
    options,
  )

export const useIncomePerCustomerReport = ({ payload, options }) =>
  useReportQuery('incomePerCustomerReports', incomePerCustomerReports, payload, options)

export const useIncomePerCustomerDetailReport = ({ payload, options }) =>
  useReportQuery('incomePerCustomerDetailReports', incomePerCustomerDetailReports, payload, options)

export const usePurchasesPerVendorReport = ({ payload, options }) =>
  useReportQuery('purchasesPerVendorReports', purchasesPerVendorReports, payload, options)

export const usePurchasesPerVendorDetailReport = ({ payload, options }) =>
  useReportQuery(
    'purchasesPerVendorDetailReports',
    purchasesPerVendorDetailReports,
    payload,
    options,
  )

export const useProfitabilityPerInvoiceReport = ({ payload, options }) =>
  useReportQuery('profitabilityPerInvoiceReports', profitabilityPerInvoiceReports, payload, options)

export const useProfitabilityPerInvoiceGrandTotalReport = ({ payload, options }) =>
  useReportQuery(
    'profitabilityPerInvoiceGrandTotalReports',
    profitabilityPerInvoiceGrandTotalReports,
    payload,
    options,
  )

export const useSalesPerPersonReport = ({ payload, options }) =>
  useReportQuery('salesPerPersonReports', salesPerPersonReports, payload, options)

export const useSalesPerPersonDailyReport = ({ payload, options }) =>
  useReportQuery('salesPerPersonDailyReports', salesPerPersonDailyReports, payload, options)

export const useSalesPerPeriodReport = ({ payload, options }) =>
  useReportQuery('salesPerPeriodReports', salesPerPeriodReports, payload, options)

export const usePurchasesPerPeriodReport = ({ payload, options }) =>
  useReportQuery('purchasesPerPeriodReports', purchasesPerPeriodReports, payload, options)

export const useSalesPerProductCategoryReport = ({ payload, options }) =>
  useReportQuery('getSalesPerProductCategory', getSalesPerProductCategory, payload, options)

export const useSalesPerProductCategoryDetailReport = ({ id, payload, options }) =>
  useReportQuery(
    'getSalesPerProductCategoryDetail',
    (p) => getSalesPerProductCategoryDetail({ id, params: p }),
    payload,
    options,
    undefined,
    [id],
  )

export const useProductPurchasesContactsReport = ({ payload, options }) =>
  useReportQuery('getProductPurchasesContacts', getProductPurchasesContacts, payload, options)

export const useProductSalesContactsReport = ({ payload, options }) =>
  useReportQuery('getProductSalesContacts', getProductSalesContacts, payload, options)

export const useInvoicePaymentReport = ({ payload, options }) =>
  useReportQuery('invoicePaymentReports', invoicePaymentReports, payload, options)

export const useInvoicePaymentTotalReport = ({ payload, options }) =>
  useReportQuery('invoicePaymentTotalReports', invoicePaymentTotalReports, payload, options)

export const usePurchaseInvoicePaymentReport = ({ payload, options }) =>
  useReportQuery('purchaseInvoicePaymentReports', purchaseInvoicePaymentReports, payload, options)

export const usePurchaseInvoicePaymentTotalReport = ({ payload, options }) =>
  useReportQuery(
    'purchaseInvoicePaymentTotalReports',
    purchaseInvoicePaymentTotalReports,
    payload,
    options,
  )

export const useCustomerPaymentRecapReport = ({ payload, options }) =>
  useReportQuery('customerPaymentRecapReports', customerPaymentRecapReports, payload, options)

export const useSalesDeliveryReport = ({ payload, options }) =>
  useReportQuery('salesDeliveryReports', salesDeliveryReports, payload, options)

export const usePurchaseDeliveryReport = ({ payload, options }) =>
  useReportQuery('purchaseDeliveryReports', purchaseDeliveryReports, payload, options)

export const useShippingCostPerExpeditionReport = ({ payload, options }) =>
  useReportQuery(
    'shippingCostPerExpeditionReports',
    shippingCostPerExpeditionReports,
    payload,
    options,
  )

export const useShippingCostPerExpeditionDailyReport = ({ payload, options }) =>
  useReportQuery(
    'shippingCostPerExpeditionDailyReports',
    shippingCostPerExpeditionDailyReports,
    payload,
    options,
  )

export const useReceivablePayableReport = ({ payload, options }) =>
  useReportQuery('receivablePayableReports', receivablePayableReports, payload, options)

export const useReceivablePayableDetailReport = ({ extraQueryKey = [], payload, options }) =>
  useReportQuery(
    'receivablePayableDetailReports',
    receivablePayableDetailReports,
    payload,
    options,
    undefined,
    extraQueryKey,
  )

export const useEquityMovementReport = ({ payload, options }) =>
  useReportQuery('equityMovementReports', equityMovementReports, payload, options)

export const useProfitLossReport = ({ payload, options }) =>
  useReportQuery('profitLossReports', profitLossReports, payload, options)

export const useGeneralLedgerStatsReport = ({ payload, options }) =>
  useReportQuery('generalLedgerStatsReports', generalLedgerStatsReports, payload, options)

export const useGeneralLedgerReport = ({ payload, options }) =>
  useReportQuery('generalLedgerReports', generalLedgerReports, payload, options)

export const useGeneralLedgerAccountsReport = ({ payload, options }) =>
  useReportQuery('generalLedgerAccountsReports', generalLedgerAccountsReports, payload, options)

export const useSubsidiaryLedgerReport = ({ payload, options }) =>
  useReportQuery('subsidiaryLedgerReports', subsidiaryLedgerReports, payload, options)

export const useGeneralLedgerAccountsDetailReport = ({ payload, options = {} }) =>
  useReportQuery(
    'generalLedgerAccountsDetailReports',
    (p) => generalLedgerAccountsDetailReports(p.accountId, p.params),
    payload,
    options,
  )

export const useInventoryProductSerialStockMovementDetailReport = ({ payload, options }) =>
  useReportQuery(
    'inventoryProductSerialStockMovementDetailReport',
    inventoryProductSerialStockMovementDetailReports,
    payload,
    options,
  )

export const useInventoryProductSerialExpiryAgeReports = ({ payload, options }) =>
  useReportQuery(
    'inventoryProductSerialExpiryAgeReports',
    inventoryProductSerialExpiryAgeReports,
    payload,
    options,
  )

export const useInventoryProductSerialHistoryReports = ({ payload, options }) =>
  useReportQuery(
    'inventoryProductSerialHistoryReports',
    inventoryProductSerialHistoryReports,
    payload,
    options,
  )

export const useInventoryProductSerialStockDetailReport = ({ payload, options }) =>
  useReportQuery(
    'inventoryProductSerialStockDetailReports',
    inventoryProductSerialStockDetailReports,
    payload,
    options,
  )

export const useInventoryProductSerialExpiresSoonReport = ({ payload, options }) =>
  useReportQuery(
    'inventoryProductSerialExpiresSoonReports',
    inventoryProductSerialExpiresSoonReports,
    payload,
    options,
  )

export const useStampsReport = ({ payload, enabled = false }) =>
  useReportQuery('dutyStampsReports', dutyStampsReports, payload, { enabled })

export const useGetReportActivityTeamReport = ({ payload, options }) =>
  useReportQuery('activityTeamReports', getActivityTeamReports, payload, options)

export const useGetReportActivityTeamTransactions = ({ payload, options }) =>
  useReportQuery(
    'activityTeamTransactionReports',
    getActivityTeamTransactionReports,
    payload,
    options,
  )

export const usePurchaseOrderPerProductReport = ({ payload, options }) =>
  useReportQuery('purchaseOrderPerProductReports', purchaseOrderPerProductReports, payload, options)

export const usePurchaseOrderPerProductDetailReport = ({ payload, options }) =>
  useReportQuery(
    'purchaseOrderPerProductDetailReports',
    purchaseOrderPerProductDetailReports,
    payload,
    options,
  )

export const useConsolidationBalanceSheetReport = ({ payload, options }) =>
  useReportQuery(
    'consolidationBalanceSheetReports',
    consolidationBalanceSheetReports,
    payload,
    options,
  )

export const useConsolidationBalanceSheetDetailReport = ({ payload, options }) =>
  useReportQuery(
    'consolidationBalanceSheetDetailReports',
    consolidationBalanceSheetDetailReports,
    payload,
    options,
  )

const balanceSheetTransform = (raw) => {
  const printUrl = raw?.print_url
  if (raw && 'print_url' in raw) {
    delete raw.print_url
  }
  return formatBalanceSheet({ data: raw, print_url: printUrl })
}

export const useBalanceSheetReport = ({ payload, options }) =>
  useReportQuery(
    'balanceSheetReports',
    balanceSheetReports,
    payload,
    options,
    balanceSheetTransform,
  )

export const useConsolidationCashFlowReport = ({ payload, options }) =>
  useReportQuery('consolidationCashFlowReports', consolidationCashFlowReports, payload, options)

export const useConsolidationProfitLossReport = ({ payload, options }) =>
  useReportQuery('consolidationProfitLossReports', consolidationProfitLossReports, payload, options)

export const useConsolidationWorksheetReport = ({ payload, options }) =>
  useReportQuery('consolidationWorksheetReports', consolidationWorksheetReports, payload, options)

export const useGeneralLedgerOverviewReport = ({ payload, options }) =>
  useReportQuery('generalLedgerOverviewReports', getGeneralLedgerOverviewReports, payload, options)

export const useBankSummaryReport = ({ payload, options }) =>
  useReportQuery('bankSummaryReports', bankSummaryReports, payload, options)

export const useJournalReport = ({ payload, options }) =>
  useReportQuery('journalReports', journalReports, payload, options)

export const useTrialBalanceReport = ({ payload, options }) =>
  useReportQuery('trialBalanceReports', trialBalanceReports, payload, options)

export const useCashFlowEndOfPeriod = ({ payload, options }) =>
  useReportQuery('cashFlowEndOfPeriod', getCashFlowEndOfPeriod, payload, options)

export const useCashFlowProjectionReport = ({ payload, options }) =>
  useReportQuery('cashFlowProjectionReports', cashFlowProjectionReports, payload, options)

export const useCreditMemoReport = ({ payload, options }) =>
  useReportQuery('creditMemoReports', creditMemoReports, payload, options)

export const useDebitMemoReport = ({ payload, options }) =>
  useReportQuery('debitMemoReports', debitMemoReports, payload, options)

export const useBalanceReceivableTerminReport = ({ payload, options }) =>
  useReportQuery('balanceReceivableTerminReports', balanceReceivableTerminReports, payload, options)

export const useBalanceReceivableTerminDetailReport = ({ payload, options }) =>
  useReportQuery(
    'balanceReceivableTerminDetailReports',
    balanceReceivableTerminDetailReports,
    payload,
    options,
  )

export const useBalancePayableTerminReport = ({ payload, options }) =>
  useReportQuery('balancePayableTerminReports', balancePayableTerminReports, payload, options)

export const useBalancePayableTerminDetailReport = ({ payload, options }) =>
  useReportQuery(
    'balancePayableTerminDetailReports',
    balancePayableTerminDetailReports,
    payload,
    options,
  )

export const useSalesPerRegionReport = ({ payload, options }) =>
  useReportQuery('salesPerRegionReports', salesPerRegionReports, payload, options)

export const usePurchasePerRegionReport = ({ payload, options }) =>
  useReportQuery('purchasePerRegionReports', purchasePerRegionReports, payload, options)

export const useProductsPriceHistoryReport = ({ payload, options }) =>
  useReportQuery(
    'productsPriceHistoryReports',
    (p) => productsPriceHistoryReports({ params: p }),
    payload,
    options,
  )

export const useInventoryTurnoverReport = ({ payload, options }) =>
  useReportQuery('inventoryTurnoverReports', inventoryTurnoverReports, payload, options)

export const useInventoryTurnoverDetailReport = ({ id, params, options }) =>
  useReportQuery(
    'inventoryTurnoverDetailReports',
    (p) => inventoryTurnoverDetailReports({ id, params: p }),
    params,
    options,
    undefined,
    [id],
  )

export const usePaymentConnectKledoDisbursment = (params = {}, options = {}) => {
  return useQuery(
    ['paymentConnectKledoDisbursment', params],
    async () => {
      const {
        data: { data },
      } = await paymentConnectKledoDisbursment(params)
      return data
    },
    {
      onError: (error) => message.error(error?.data?.message || 'Failed data from server!'),
      ...options,
    },
  )
}

export const usePaymentConnectKledoDisbursmentListDetail = (params = {}, options = {}) => {
  return useQuery(
    ['paymentConnectKledoDisbursmentListDetail', params],
    async () => {
      const {
        data: { data },
      } = await paymentConnectKledoDisbursmentListDetail(params)
      return data
    },
    {
      onError: (error) => message.error(error?.data?.message || 'Failed data from server!'),
      ...options,
    },
  )
}

export const usePaymentConnectKledoTransaction = (params = {}, options = {}) => {
  return useQuery(
    ['paymentConnectKledoTransaction', params],
    async () => {
      const {
        data: { data },
      } = await paymentConnectKledoTransaction(params)
      return data
    },
    {
      onError: (error) => message.error(error?.data?.message || 'Failed data from server!'),
      ...options,
    },
  )
}

export const usePaymentConnectKledoTransactionListDetail = (params = {}, options = {}) => {
  return useQuery(
    ['paymentConnectKledoTransactionListDetail', params],
    async () => {
      const {
        data: { data },
      } = await paymentConnectKledoTransactionListDetail(params)
      return data
    },
    {
      onError: (error) => message.error(error?.data?.message || 'Failed data from server!'),
      ...options,
    },
  )
}

// material

export const useRequestFulfillmentReport = ({ payload, options }) =>
  useReportQuery('requestFulfillmentReports', requestFulfillmentReports, payload, options)

export const useUsageHistoryReport = ({ payload, options }) =>
  useReportQuery('usageHistoryReports', usageHistoryReports, payload, options)

export const useUsagePerProjectReport = ({ payload, options }) =>
  useReportQuery('usagePerProjectReports', usagePerProjectReports, payload, options)

export const usePricelistUnitConversionReport = ({ payload, options }) =>
  useReportQuery('pricelistUnitConversionReports', pricelistUnitConversionReports, payload, options)
